import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faSpinner, faCheckCircle, faClock } from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CommonTable from "../../../../common/table/ComonTable";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import apiClient from "../../../../../services/apiClient";
import apiServices from "../../../../../services/apiService";
import storageService from "../../../../../utils/storageService";

const StaffApprovalsDashboard = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);

    // Typically Unit Admins will have a branchorunitID
    const unitId = user?.branchorunitID || user?.unitId;
    const siteId = user?.siteId || Number(storageService.getItem("siteId")) || 2;
    const approverName = user?.name || user?.userName || "UnitAdmin";

    const userRoleId = Number(storageService.getItem("roleId") || user?.roleId || 0);
    const personTypeId = Number(storageService.getItem("personTypeId"));
    const isVendorAdmin = userRoleId === 7 || personTypeId === 4;
    const vendorId = isVendorAdmin ? (user?.userId || user?.Id || user?.id || storageService.getItem("employeeCode")) : null;

    const [loading, setLoading] = useState(false);
    const [requests, setRequests] = useState([]);

    const [actionModal, setActionModal] = useState({
        isOpen: false,
        allotmentId: null,
        actionType: "", // "Approve" or "Reject"
        remarks: "",
        staffName: ""
    });

    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const fetchPendingRequests = useCallback(async () => {
        setLoading(true);
        try {
            // Assuming an endpoint exists to fetch pending approvals for a unit
            const response = await apiClient.get('/staff-management/staff/GetPendingApprovals', {
                params: {
                    ...(isVendorAdmin ? { vendorId: Number(vendorId) } : { unitId: unitId }),
                    siteId: siteId,
                    status: 'Pending Approval',
                    pageNumber: pagination.pageNumber,
                    pageSize: pagination.pageSize
                }
            });

            if (response.data?.isSuccess && response.data?.data) {
                setRequests(response.data.data);
                setPagination(prev => ({
                    ...prev,
                    totalRecords: response.data.totalCount || response.data.data.length
                }));
            } else {
                setRequests([]);
            }
        } catch (error) {
            console.error("Failed to fetch pending approvals:", error);
            // Since this is a new feature, the endpoint might not be perfectly mapped in this mock. 
            // We swallow the error and show empty state if it fails.
            setRequests([]);
        } finally {
            setLoading(false);
        }
    }, [unitId, siteId, pagination.pageNumber, pagination.pageSize]);

    useEffect(() => {
        if (unitId || siteId) {
            fetchPendingRequests();
        }
    }, [fetchPendingRequests, unitId, siteId]);

    const handleProcessRequest = async () => {
        if (!actionModal.allotmentId) return;

        try {
            const payload = {
                allotmentId: actionModal.allotmentId,
                action: actionModal.actionType, // "Approve" or "Reject"
                remarks: actionModal.remarks || `${actionModal.actionType}d by ${approverName}`,
                approverName: approverName
            };

            const response = await apiClient.post('/staff-management/staff/ProcessAccessRequest', payload);

            if (response.data?.isSuccess) {
                toast.success(`Request ${actionModal.actionType}d successfully.`);
                setActionModal({ isOpen: false, allotmentId: null, actionType: "", remarks: "", staffName: "" });
                fetchPendingRequests();
            } else {
                toast.error(response.data?.message || `Failed to ${actionModal.actionType} request.`);
            }
        } catch (error) {
            console.error("Error processing request:", error);
            toast.error(error.response?.data?.message || `An error occurred while trying to ${actionModal.actionType} the request.`);
        }
    };

    const tableHeaders = [
        { label: "Request ID", key: "allotmentId" },
        { label: "Staff Name", key: "staffName" },
        { label: "Vendor", key: "vendorName" },
        { label: "Unit", key: "unitNumber" },
        { label: "Requested Period", key: "period" },
        { label: "Status", key: "statusLabel" },
        { label: "Actions", key: "actions" }
    ];

    const renderTableCell = (key, row) => {
        if (key === "staffName") {
            const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
            return <span className="text-sm font-medium text-gray-800 dark:text-textsecondary">{name || "—"}</span>;
        }
        if (key === "period") {
            const from = row.startDate ? new Date(row.startDate).toLocaleDateString() : 'N/A';
            const to = row.endDate ? new Date(row.endDate).toLocaleDateString() : 'N/A';
            return <span className="text-xs text-gray-600">{`${from} - ${to}`}</span>;
        }
        if (key === "statusLabel") {
            return (
                <span className="px-2.5 py-1 text-[10px] font-bold rounded-full bg-orange-100 text-orange-700 border border-orange-200 flex items-center gap-1.5 w-fit">
                    <FontAwesomeIcon icon={faClock} />
                    Pending Approval
                </span>
            );
        }
        return <span className="text-sm font-medium text-gray-800 dark:text-textsecondary">{row[key] || "—"}</span>;
    };

    const customActions = isVendorAdmin ? [] : [
        {
            label: "Approve Request",
            icon: faCheck,
            onClick: (row) => setActionModal({ isOpen: true, allotmentId: row.allotmentId || row.id, actionType: "Approve", remarks: "", staffName: `${row.firstName || ''} ${row.lastName || ''}`.trim() })
        },
        {
            label: "Reject Request",
            icon: faTimes,
            onClick: (row) => setActionModal({ isOpen: true, allotmentId: row.allotmentId || row.id, actionType: "Reject", remarks: "", staffName: `${row.firstName || ''} ${row.lastName || ''}`.trim() })
        }
    ];

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide relative">
            <Breadcrumbs items={["Workforce Management", "Approvals", "Pending Requests"]} />

            <PageHeadingSection
                Navbar={true}
                mainHeading="Pending Staff Access Requests"
                subHeading="Review and approve/reject staff duty allotments for your units under the strict policy engine."
                primaryBtnText="Manage Pre-Approvals"
                primaryBtnIcon={faCheckCircle}
                PrimaryOnClick={() => navigate("/zentres/workforce-management/approvals/pre-approvals")}
            />

            <div className="shrink-0 flex-1 flex flex-col mt-6">
                <CommonTable
                    loading={loading}
                    tableHeading="Pending Requests"
                    headers={tableHeaders}
                    data={requests}
                    renderCell={renderTableCell}
                    customActions={customActions}
                    pagination={{
                        pageNumber: pagination.pageNumber,
                        pageSize: pagination.pageSize,
                        totalRecords: pagination.totalRecords,
                        totalPages: Math.ceil(pagination.totalRecords / pagination.pageSize) || 1
                    }}
                    onPageChange={(p) => setPagination(prev => ({ ...prev, pageNumber: p }))}
                    onLimitChange={(l) => setPagination(prev => ({ ...prev, pageSize: l, pageNumber: 1 }))}
                    isLoading={loading}
                />
            </div>

            {/* Action Modal */}
            {actionModal.isOpen && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className={`px-6 py-4 border-b ${actionModal.actionType === 'Approve' ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-red-50 border-red-100 text-red-800'}`}>
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <FontAwesomeIcon icon={actionModal.actionType === 'Approve' ? faCheckCircle : faTimes} />
                                {actionModal.actionType} Request
                            </h3>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                You are about to <strong>{actionModal.actionType.toLowerCase()}</strong> access for <strong>{actionModal.staffName}</strong>.
                            </p>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Remarks (Optional)</label>
                                <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    rows="3"
                                    placeholder="Add any remarks for this action..."
                                    value={actionModal.remarks}
                                    onChange={(e) => setActionModal(prev => ({ ...prev, remarks: e.target.value }))}
                                ></textarea>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                            <button
                                onClick={() => setActionModal({ isOpen: false, allotmentId: null, actionType: "", remarks: "", staffName: "" })}
                                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleProcessRequest}
                                className={`px-4 py-2 rounded-md text-sm font-bold text-white transition-colors ${actionModal.actionType === 'Approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
                            >
                                Confirm {actionModal.actionType}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffApprovalsDashboard;
